import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge, Accordion} from 'react-bootstrap';
import { FaSave, FaSync, FaCheckCircle, FaCog, FaEnvelope, FaCreditCard, FaStar, FaChartBar, FaTrash, FaBolt } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import settingsService from '../services/settingsService';

function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInitializeConfirm, setShowInitializeConfirm] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [categories, setCategories] = useState({});
  const [changedSettings, setChangedSettings] = useState({});
  const [cacheStats, setCacheStats] = useState(null);
  const [loadingCacheStats, setLoadingCacheStats] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [invalidatePattern, setInvalidatePattern] = useState('');
  const [invalidating, setInvalidating] = useState(false);
  const [cacheMessage, setCacheMessage] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchCacheStats();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await settingsService.getCategories();
      setCategories(data.categories || {});

      if (Object.keys(data.categories || {}).length === 0) {
        try {
          await settingsService.initializeDefaults();
          const retryData = await settingsService.getCategories();
          setCategories(retryData.categories || {});
        } catch (initError) {
          console.error('Failed to initialize defaults:', initError);
        }
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      if (err.response?.status === 403) {
        setError('Admin access required. Please log in as an administrator.');
      } else if (err.response?.status === 401) {
        setError('Authentication required. Please log in first.');
      } else {
        setError('Failed to load settings. ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCacheStats = async () => {
    try {
      setLoadingCacheStats(true);
      const res = await settingsService.getCacheStats();
      setCacheStats(res.cache_stats || null);
    } catch (err) {
      console.error('Failed to load cache stats', err);
      setCacheStats(null);
    } finally {
      setLoadingCacheStats(false);
    }
  };

  const handleClearAllCache = async () => {
    if (!window.confirm('Clear all cache entries? This will flush the in-process cache.')) return;
    try {
      setClearingCache(true);
      setCacheMessage('');
      const res = await settingsService.clearCache();
      setCacheMessage(`Cleared ${res.cleared} entries`);
      await fetchCacheStats();
    } catch (err) {
      console.error('Failed to clear cache', err);
      setCacheMessage('Failed to clear cache');
    } finally {
      setClearingCache(false);
      setTimeout(() => setCacheMessage(''), 4000);
    }
  };

  const handleInvalidatePattern = async () => {
    if (!invalidatePattern || invalidatePattern.trim() === '') return setCacheMessage('Enter a pattern to invalidate.');
    try {
      setInvalidating(true);
      setCacheMessage('');
      const res = await settingsService.clearCache(invalidatePattern.trim());
      setCacheMessage(`Cleared ${res.cleared} entries for pattern ${res.pattern || ''}`);
      setInvalidatePattern('');
      await fetchCacheStats();
    } catch (err) {
      console.error('Failed to invalidate pattern', err);
      setCacheMessage('Failed to invalidate pattern');
    } finally {
      setInvalidating(false);
      setTimeout(() => setCacheMessage(''), 4000);
    }
  };

  const handleSettingChange = (key, value, dataType) => {
    setChangedSettings(prev => ({
      ...prev,
      [key]: { value, dataType }
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const settingsToUpdate = {};
      Object.entries(changedSettings).forEach(([key, data]) => {
        settingsToUpdate[key] = data.value;
      });

      await settingsService.bulkUpdate(settingsToUpdate);

      setSuccess('Settings saved successfully!');
      setChangedSettings({});

      await fetchSettings();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeClick = () => {
    setShowInitializeConfirm(true);
  };

  const handleInitializeConfirm = async () => {
    setInitializing(true);
    try {
      setLoading(true);
      setError('');
      await settingsService.initializeDefaults();
      setSuccess('Default settings initialized successfully!');
      await fetchSettings();
      setTimeout(() => setSuccess(''), 3000);
      setShowInitializeConfirm(false);
    } catch (err) {
      console.error('Failed to initialize settings:', err);
      setError('Failed to initialize settings. Please try again.');
      setShowInitializeConfirm(false);
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  };

  const renderSettingControl = (setting) => {
    const currentValue = changedSettings[setting.key]?.value ?? setting.value;

    if (setting.data_type === 'boolean') {
      return (
        <Form.Check
          type="switch"
          id={`setting-${setting.key}`}
          label={currentValue ? 'Enabled' : 'Disabled'}
          checked={!!currentValue}
          onChange={(e) => handleSettingChange(setting.key, e.target.checked, 'boolean')}
        />
      );
    }

    if (setting.data_type === 'integer') {
      return (
        <Form.Control
          type="number"
          value={currentValue ?? 0}
          onChange={(e) => handleSettingChange(setting.key, parseInt(e.target.value) || 0, 'integer')}
        />
      );
    }

    if (setting.data_type === 'float') {
      return (
        <Form.Control
          type="number"
          step="0.01"
          value={currentValue ?? 0.0}
          onChange={(e) => handleSettingChange(setting.key, parseFloat(e.target.value) || 0.0, 'float')}
        />
      );
    }

    if (setting.data_type === 'json') {
      const textValue = typeof currentValue === 'string' ? currentValue : JSON.stringify(currentValue || {}, null, 2);
      return (
        <Form.Control
          as="textarea"
          rows={6}
          value={textValue}
          onChange={(e) => {
            const v = e.target.value;
            try {
              const parsed = JSON.parse(v);
              handleSettingChange(setting.key, parsed, 'json');
            } catch (err) {
              handleSettingChange(setting.key, v, 'json');
            }
          }}
        />
      );
    }

    if (/(password|secret|key|token)/i.test(setting.key)) {
      return (
        <Form.Control
          type="password"
          value={currentValue ?? ''}
          onChange={(e) => handleSettingChange(setting.key, e.target.value, 'string')}
        />
      );
    }

    return (
      <Form.Control
        type="text"
        value={currentValue ?? ''}
        onChange={(e) => handleSettingChange(setting.key, e.target.value, 'string')}
      />
    );
  };

  const getCategoryIcon = (category) => {
    const iconProps = { size: 18, className: 'text-primary' };
    switch (category) {
      case 'features':
        return <FaStar {...iconProps} />;
      case 'payment':
        return <FaCreditCard {...iconProps} />;
      case 'analytics':
        return <FaChartBar {...iconProps} />;
      case 'email':
        return <FaEnvelope {...iconProps} />;
      case 'general':
        return <FaCog {...iconProps} />;
      default:
        return <FaCog {...iconProps} />;
    }
  };

  const getCategoryTitle = (category) => {
    return category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ');
  };

  if (loading) {
    return (
      <Container fluid className="px-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  const hasChanges = Object.keys(changedSettings).length > 0;
  const settingsHaveValues = Object.keys(categories || {}).some((categoryName) => {
    const settings = categories[categoryName];
    return Array.isArray(settings) && settings.some(s => s && s.value !== null && s.value !== undefined && s.value !== '');
  });

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Settings"
        subtitle="Manage application features and configuration"
      />

      <Row className="mb-3">
        <Col>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              <FaCheckCircle className="me-2" />
              {success}
            </Alert>
          )}
        </Col>
      </Row>

      {Object.keys(categories).length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <h5 className="text-muted mb-3">No Settings Found</h5>
            <p className="text-muted mb-4">Initialize default settings to get started.</p>
            <Button variant="primary" onClick={handleInitializeClick}>
              <FaSync className="me-2" />
              Initialize Default Settings
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <div>
                {hasChanges && (
                  <Badge bg="warning" className="me-2">
                    {Object.keys(changedSettings).length} unsaved change(s)
                  </Badge>
                )}
              </div>
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={handleInitializeClick}
                  disabled={settingsHaveValues}
                  title={settingsHaveValues ? 'Disabled: settings already contain values' : 'Initialize default settings'}
                >
                  <FaSync className="me-1" />
                  Initialize Defaults
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveSettings}
                  disabled={!hasChanges || saving}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>

          <Accordion
            alwaysOpen
            defaultActiveKey={[...Object.keys(categories || {}).map((_, i) => i.toString()), 'cache']}
          >
            <Accordion.Item eventKey="cache">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <span className="me-2"><FaBolt size={18} className="text-primary"/></span>
                  <strong>Cache</strong>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="border h-100">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-1">Cache Statistics</h6>
                            <small className="text-muted">In-process cache metrics</small>
                          </div>
                          <div />
                        </div>

                        {loadingCacheStats ? (
                          <div className="py-3 text-center"><Spinner animation="border" size="sm"/></div>
                        ) : cacheStats ? (
                          <div>
                            <Row>
                              <Col xs={6}><small className="text-muted">Hits</small><div><strong>{cacheStats.hits}</strong></div></Col>
                              <Col xs={6}><small className="text-muted">Misses</small><div><strong>{cacheStats.misses}</strong></div></Col>
                            </Row>
                            <Row className="mt-2">
                              <Col xs={6}><small className="text-muted">Entries (keys)</small><div><strong>{cacheStats.size}</strong></div></Col>
                              <Col xs={6}><small className="text-muted">Hit Rate</small><div><strong>{cacheStats.hit_rate}%</strong></div></Col>
                            </Row>
                            <Row className="mt-2">
                              <Col xs={12}><small className="text-muted">Note: "Entries" is the number of cached keys stored in the in-process cache; memory usage is not tracked.</small></Col>
                            </Row>
                          </div>
                        ) : (
                          <div className="text-muted">Cache stats unavailable</div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="border h-100">
                      <Card.Body>
                        <h6 className="mb-2">Invalidate Cache</h6>
                        <small className="text-muted">Manage the in-process cache</small>

                        <div className="mt-3">
                          <label className="form-label fw-semibold">Invalidate by pattern</label>
                          <small className="text-muted d-block mb-1">Remove cache entries matching a simple pattern (e.g. <code>product:*</code>).</small>
                          <div className="d-flex">
                            <Form.Control
                              className="me-2"
                              placeholder="pattern e.g. product:*"
                              value={invalidatePattern}
                              onChange={(e) => setInvalidatePattern(e.target.value)}
                            />
                            <Button variant="outline-primary" onClick={handleInvalidatePattern} disabled={invalidating || !invalidatePattern} style={{ minWidth: '140px' }}>
                              {invalidating ? <Spinner animation="border" size="sm" /> : <><FaBolt className="me-1"/> Invalidate</>}
                            </Button>
                          </div>
                        </div>

                        <div className="mt-3 d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-semibold">Invalidate All</div>
                            <small className="text-muted">Flush the entire in-process cache (all keys).</small>
                          </div>
                          <div>
                            <Button variant="outline-danger" onClick={handleClearAllCache} disabled={clearingCache} style={{ minWidth: '140px' }}>
                              {clearingCache ? <Spinner animation="border" size="sm" /> : <><FaTrash className="me-1"/> Invalidate All</>}
                            </Button>
                          </div>
                        </div>

                        {cacheMessage && <div className="mt-3"><Badge bg="info">{cacheMessage}</Badge></div>}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {Object.keys(categories || {})
              .filter(categoryName => categories[categoryName])
              .map((categoryName, index) => {
                const settings = categories[categoryName];
                return (
              <Accordion.Item eventKey={index.toString()} key={categoryName}>
                <Accordion.Header>
                  <div className="d-flex align-items-center">
                    <span className="me-2">{getCategoryIcon(categoryName)}</span>
                    <strong>{getCategoryTitle(categoryName)}</strong>
                    <Badge bg="secondary" className="ms-2">{settings.length}</Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row className="g-3">
                    {settings.map((setting) => (
                      <Col md={6} key={setting.key}>
                        <Card className="border h-100">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="flex-grow-1">
                                <h6 className="mb-1">
                                  {setting.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </h6>
                                {setting.description && (
                                  <small className="text-muted">{setting.description}</small>
                                )}
                              </div>
                              <div className="ms-2">
                                {setting.is_public ? (
                                  <Badge bg="primary" pill>Public</Badge>
                                ) : (
                                  <Badge bg="secondary" pill>Private</Badge>
                                )}
                              </div>
                            </div>
                            <div className="mt-3">
                              {renderSettingControl(setting)}
                            </div>
                            {changedSettings[setting.key] && (
                              <div className="mt-2">
                                <Badge bg="warning">Modified</Badge>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            );
            })}
          </Accordion>
        </>
      )}

      <ConfirmationModal
        show={showInitializeConfirm}
        onHide={() => setShowInitializeConfirm(false)}
        onConfirm={handleInitializeConfirm}
        title="Initialize Default Settings"
        message="This will create missing default settings but won't overwrite existing ones. Do you want to continue?"
        confirmText="Initialize"
        cancelText="Cancel"
        variant="primary"
        loading={initializing}
      />
    </Container>
  );
}

export default SettingsPage;
